/**
 * Triggers browser download for a blob.
 *
 * Uses URL.createObjectURL + anchor click with download attribute.
 */
export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/**
 * Opens a blob in a new browser tab
 *
 * Creates a temporary object URL and opens it with `window.open` using
 * `noopener,noreferrer`. The object URL is revoked after a short delay so the
 * newly opened tab has time to load the content before the URL is released,
 * preventing a memory leak.
 *
 * @param blob - File blob to open (e.g. a PDF fetched through the authenticated
 *   HTTP client)
 */
export function openBlobInNewTab(blob: Blob): void {
  const blobUrl = URL.createObjectURL(blob);
  window.open(blobUrl, '_blank', 'noopener,noreferrer');
  // Delay revocation so the new tab can finish loading the blob.
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}

// Hostnames allowed as pre-signed download targets.
// S3 (AWS): *.s3.amazonaws.com / *.s3.<region>.amazonaws.com
// LocalStack (local dev): localhost
const ALLOWED_DOWNLOAD_HOSTNAMES = [/\.s3\.amazonaws\.com$/, /\.s3\.[a-z0-9-]+\.amazonaws\.com$/, /^localhost$/];

function validateDownloadUrl(raw: string): URL {
  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    throw new Error('Invalid download URL');
  }

  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
    throw new Error('Invalid download URL protocol');
  }

  // Block path traversal attempts
  if (raw.includes('/../') || /\/%2e%2e\//i.test(raw)) {
    throw new Error('Invalid download URL path');
  }

  const allowed = ALLOWED_DOWNLOAD_HOSTNAMES.some((pattern) => pattern.test(parsed.hostname));
  if (!allowed) {
    throw new Error(`Download URL host not allowed: ${parsed.hostname}`);
  }

  return parsed;
}

// Fetches `url` (no auth header — intended for pre-signed S3 URLs), converts the response to a
// blob, and triggers a browser download via downloadBlob. Using a blob URL avoids the browser
// ignoring the `download` attribute for cross-origin URLs (HTML spec restriction).
export function downloadFromUrl(url: string, fileName: string): Promise<void> {
  let validated: URL;
  try {
    validated = validateDownloadUrl(url);
  } catch (err) {
    return Promise.reject(err);
  }

  return fetch(validated.href, { redirect: 'follow' })
    .then((res) => {
      if (!res.ok) throw new Error(`Download failed: ${res.status}`);
      return res.blob();
    })
    .then((blob) => downloadBlob(blob, fileName));
}
